import { ThemeEvents } from '@theme/events';

/**
 * Lightweight MicroModal-compatible API for the fortnight payment modal.
 * Matches the open/close class and aria-hidden behavior expected by the existing CSS.
 */
const FortnightMicroModal = {
  /**
   * @param {string} modalId
   * @param {{ disableScroll?: boolean, awaitCloseAnimation?: boolean }} [options]
   */
  show(modalId, options = {}) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');

    if (options.disableScroll !== false) {
      document.documentElement.setAttribute('data-fortnight-scroll-lock', '');
    }

    const focusTarget = modal.querySelector('.fortnight-payment-modal__close');
    focusTarget?.focus();
  },

  /**
   * @param {string} modalId
   * @param {{ awaitCloseAnimation?: boolean }} [options]
   */
  close(modalId, options = {}) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');

    const finishClose = () => {
      modal.classList.remove('is-open');
      document.documentElement.removeAttribute('data-fortnight-scroll-lock');
    };

    if (options.awaitCloseAnimation) {
      window.setTimeout(finishClose, 300);
    } else {
      finishClose();
    }
  },
};

if (typeof window.MicroModal === 'undefined') {
  window.MicroModal = FortnightMicroModal;
}

const MODAL_ID = 'fortnight-payment-modal';

/**
 * Opens the fortnight payment modal when configured payment methods exist.
 * @param {Event} event
 */
function openFortnightModal(event) {
  event.preventDefault();

  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  const paymentSections = modal.querySelectorAll('.fortnight-payment-modal__section');
  if (paymentSections.length === 0) return;

  const show = window.MicroModal?.show || FortnightMicroModal.show;
  show(MODAL_ID, {
    disableScroll: true,
    awaitCloseAnimation: true,
  });
}

/**
 * Closes the fortnight payment modal.
 * @param {Event} [event]
 */
function closeFortnightModal(event) {
  event?.preventDefault();
  event?.stopPropagation();

  const close = window.MicroModal?.close || FortnightMicroModal.close;
  close(MODAL_ID, { awaitCloseAnimation: true });
}

/**
 * Updates fortnight installment price from section-rendered HTML on variant change.
 * @extends {HTMLElement}
 */
class FortnightPayment extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    this.section = section;
    this.productId = this.dataset.productId;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    this.querySelectorAll('[data-fortnight-modal-open]').forEach((button) => {
      button.addEventListener('click', openFortnightModal);
    });
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.productId = String(event.detail.data.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!(html instanceof Document || html instanceof DocumentFragment || html instanceof Element)) return;

    const nextPrice = html.querySelector(
      `fortnight-payment[data-block-id="${this.dataset.blockId}"] [data-fortnight-price]`
    );
    const currentPrice = this.querySelector('[data-fortnight-price]');
    if (nextPrice && currentPrice) {
      currentPrice.textContent = nextPrice.textContent;
    }
  };
}

/**
 * Initializes modal open/close handlers and mounts the modal on body once.
 */
function initFortnightModal() {
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  if (modal.parentNode !== document.body) {
    document.body.appendChild(modal);
  }

  modal.querySelectorAll('[data-micromodal-close]').forEach((el) => {
    el.addEventListener('click', (event) => {
      if (el === event.target || el.classList.contains('fortnight-payment-modal__close')) {
        closeFortnightModal(event);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) {
      closeFortnightModal(event);
    }
  });
}

if (!customElements.get('fortnight-payment')) {
  customElements.define('fortnight-payment', FortnightPayment);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFortnightModal, { once: true });
} else {
  initFortnightModal();
}
